import { useRef, useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SignaturePad from "signature_pad";
import { toast } from "@/hooks/use-toast";
import { Eraser, CheckCircle, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { ServiceOrderDetail } from "@/types/service";

interface ServiceOrderSignatureDialogProps {
  order: ServiceOrderDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSignatureComplete: () => void;
  /** Pré-preenche o nome do responsável ao abrir (opcional; campo segue editável). */
  initialName?: string;
  /** Pré-preenche o CPF/CNPJ ao abrir (opcional; campo segue editável). */
  initialDocument?: string;
}

export const ServiceOrderSignatureDialog = ({
  order,
  open,
  onOpenChange,
  onSignatureComplete,
  initialName,
  initialDocument,
}: ServiceOrderSignatureDialogProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [customerDocument, setCustomerDocument] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);

  // Redimensiona o canvas corretamente (mobile + retina)
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signaturePadRef.current) return;

    const signatureData = signaturePadRef.current.isEmpty()
      ? null
      : signaturePadRef.current.toData();

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }

    signaturePadRef.current.clear();

    if (signatureData) {
      signaturePadRef.current.fromData(signatureData);
    }
  }, []);

  // Callback ref para detectar quando o canvas é montado
  const setCanvasCallback = useCallback((canvas: HTMLCanvasElement | null) => {
    canvasRef.current = canvas;
    if (canvas && open) {
      setCanvasReady(true);
    }
  }, [open]);

  // Reset quando o dialog fecha
  useEffect(() => {
    if (!open) {
      setCanvasReady(false);
      setCustomerName("");
      setCustomerDocument("");
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    }
  }, [open]);

  // Pré-preenche nome/documento a partir das props na transição para aberto.
  // Só age quando `open` vira true; não sobrescreve o que o usuário digitar depois.
  useEffect(() => {
    if (open) {
      if (initialName !== undefined) setCustomerName(initialName);
      if (initialDocument !== undefined) setCustomerDocument(initialDocument);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Listener de resize para mudança de orientação
  useEffect(() => {
    if (!open) return;

    window.addEventListener('resize', resizeCanvas);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [open, resizeCanvas]);

  // Inicializa SignaturePad quando canvas está pronto
  useEffect(() => {
    if (canvasReady && canvasRef.current && open) {
      const canvas = canvasRef.current;

      const timer = setTimeout(() => {
        try {
          if (signaturePadRef.current) {
            signaturePadRef.current.off();
          }

          signaturePadRef.current = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5,
          });

          resizeCanvas();
        } catch (error) {
          console.error('Error creating SignaturePad:', error);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [canvasReady, open, resizeCanvas]);

  const handleClear = () => {
    signaturePadRef.current?.clear();
  };

  const handleConfirm = async () => {
    if (!order) return;

    // Nome do responsável é obrigatório
    if (!customerName.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, informe o nome do responsável.",
        variant: "destructive",
      });
      return;
    }

    // CPF/CNPJ é OPCIONAL na coleta — não bloqueia a confirmação

    if (!signaturePadRef.current) {
      toast({
        title: "Erro interno",
        description: "SignaturePad não inicializado.",
        variant: "destructive",
      });
      return;
    }

    if (signaturePadRef.current.isEmpty()) {
      toast({
        title: "Assinatura obrigatória",
        description: "Por favor, assine no campo acima.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);

      // Assinatura como base64
      const signatureData = signaturePadRef.current.toDataURL();

      // IP best-effort: se falhar, segue com null sem travar a confirmação
      let signatureIp: string | null = null;
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        signatureIp = ipData.ip ?? null;
      } catch {
        signatureIp = null;
      }

      // Grava via RPC canônica "assina uma vez" — NÃO altera status (coleta não muda status)
      const { error } = await supabase.rpc('sign_service_order', {
        p_service_order_id: order.id,
        p_signature_data: signatureData,
        p_customer_name: customerName,
        p_customer_document: customerDocument.trim() || null,
        p_signature_ip: signatureIp,
      });

      if (error) throw error;

      toast({
        title: "Assinatura registrada",
        description: "A coleta foi confirmada com sucesso.",
      });

      onSignatureComplete();
      onOpenChange(false);

      setCustomerName("");
      setCustomerDocument("");
      handleClear();
    } catch (error) {
      console.error('Erro ao salvar assinatura:', error);
      const message = error instanceof Error ? error.message : '';
      if (message.includes('service_order_already_signed')) {
        toast({
          title: "Esta OS já possui assinatura registrada.",
          variant: "destructive",
        });
        // Fecha e notifica o caller para refetchar (botão de assinar some)
        onSignatureComplete();
        onOpenChange(false);
      } else if (message.includes('invalid_signature_data')) {
        toast({
          title: "Assinatura inválida, tente novamente.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erro ao salvar assinatura",
          description: "Ocorreu um erro ao registrar a assinatura. Tente novamente.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              Assinatura do Cliente — Coleta {order.service_order_number}
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Informações da OS */}
          <div className="p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Cliente</p>
            <p className="font-medium">{order.customers?.name ?? 'Cliente não informado'}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Confirme os equipamentos deixados para coleta
            </p>
          </div>

          {/* Dados do responsável */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="so-signature-name">Nome do responsável *</Label>
              <Input
                id="so-signature-name"
                placeholder="Nome completo"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="so-signature-document">CPF/CNPJ (opcional)</Label>
              <Input
                id="so-signature-document"
                placeholder="000.000.000-00"
                value={customerDocument}
                onChange={(e) => setCustomerDocument(e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Canvas de assinatura */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Assinatura digital *</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                disabled={isSaving}
              >
                <Eraser className="h-4 w-4 mr-2" />
                Limpar
              </Button>
            </div>
            <div className="border-2 border-dashed border-border rounded-lg p-4">
              <canvas
                ref={setCanvasCallback}
                className="w-full h-32 border border-border rounded bg-white cursor-crosshair touch-none"
                style={{ touchAction: 'none' }}
              />
              <p className="text-sm text-muted-foreground mt-2 text-center">
                Desenhe sua assinatura usando o mouse, touchpad ou tela touch
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSaving}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSaving ? "Salvando..." : "Confirmar Coleta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
